/**
 * Agent stream panel — real-time streaming of agent messages, tool calls, and results.
 */

import { useRef, useEffect } from "react";
import { useThreadMessages } from "@convex-dev/agent/react";
import type { Message, MessageDoc } from "@convex-dev/agent";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  Brain,
  Loader2,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Search,
  BookOpen,
  Eye,
  Database,
  Shield,
  Flag,
  Activity,
  CircleDot,
  AlertTriangle,
  TrendingDown,
  Wrench,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { type TextSegmentType, parseAgentText, tryFormatJson } from "./shared";

// ---------------------------------------------------------------------------
// Message part types + structural narrowing helpers
// ---------------------------------------------------------------------------

/** Tool-call parts inside an assistant message's content array. */
type AssistantMessage = Extract<Message, { role: "assistant" }>;
type AssistantContentPart = Exclude<
  AssistantMessage["content"],
  string
>[number];
type ToolCallPart = Extract<AssistantContentPart, { type: "tool-call" }>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Read a string field from an untyped tool payload, if present. */
function getStringField(value: unknown, key: string): string | undefined {
  if (!isRecord(value)) return undefined;
  const field = value[key];
  return typeof field === "string" ? field : undefined;
}

/** Read an array field from an untyped tool payload, if present. */
function getArrayField(value: unknown, key: string): unknown[] | undefined {
  if (!isRecord(value)) return undefined;
  const field = value[key];
  return Array.isArray(field) ? field : undefined;
}

// ---------------------------------------------------------------------------
// Main Stream Panel
// ---------------------------------------------------------------------------

export function AgentStreamPanel({
  threadId,
  jobId,
  status,
}: {
  threadId?: string;
  jobId: Id<"jobs">;
  status: string;
}) {
  const isRunning = status === "running";
  const scrollRef = useRef<HTMLDivElement>(null);

  const threadMessages = useThreadMessages(
    api.jobs.summarization.publicQueries.listAgentThreadMessages,
    threadId ? { threadId, jobId } : "skip",
    { initialNumItems: 50, stream: true },
  );

  const messages = threadMessages?.results;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isRunning && scrollRef.current) {
      const el = scrollRef.current;
      const isNearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 200;
      if (isNearBottom) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [messages, isRunning]);

  if (!threadId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Brain className="mb-2 h-8 w-8 opacity-50" />
        <p className="text-sm">
          Agent hasn't started yet. Click "Run" to begin.
        </p>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="mb-2 h-6 w-6 animate-spin text-blue-500" />
          <p className="text-sm text-muted-foreground">
            Waiting for agent to start...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">No messages in this thread.</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-320px)]">
      <div ref={scrollRef} className="flex flex-col gap-3 pb-4 pr-4">
        {messages.map((msg) => (
          <StreamMessage key={msg.key} message={msg} />
        ))}
        {isRunning && (
          <div className="flex items-center gap-2 py-2 pl-9 text-sm text-blue-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Agent is thinking...</span>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// ---------------------------------------------------------------------------
// Stream Message
// ---------------------------------------------------------------------------

function StreamMessage({ message }: { message: MessageDoc }) {
  const msg = message.message;
  if (!msg) return null;

  const role = msg.role;

  // User messages (prompts sent to the agent)
  if (role === "user") {
    return (
      <Collapsible>
        <div className="flex gap-3">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground">
              <span className="font-medium">System Prompt</span>
              <ChevronDown className="h-3 w-3" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded-md bg-muted/30 p-3 text-xs text-muted-foreground">
                {typeof msg.content === "string"
                  ? msg.content
                  : JSON.stringify(msg.content, null, 2)}
              </div>
            </CollapsibleContent>
          </div>
        </div>
      </Collapsible>
    );
  }

  // Assistant messages
  if (role === "assistant") {
    const textParts: string[] = [];
    const toolCalls: ToolCallPart[] = [];

    if (typeof msg.content === "string") {
      textParts.push(msg.content);
    } else if (Array.isArray(msg.content)) {
      for (const part of msg.content) {
        if (part.type === "text" && part.text) textParts.push(part.text);
        else if (part.type === "tool-call") toolCalls.push(part);
      }
    }

    const fullText = textParts.join("\n");
    const hasText = fullText.trim().length > 0;
    const segments = hasText ? parseAgentText(fullText) : [];

    return (
      <div className="flex gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Brain className="h-3.5 w-3.5 text-primary" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {segments.map((seg, i) => (
            <TextSegment key={i} segment={seg} />
          ))}
          {toolCalls.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {toolCalls.map((tc, i) => (
                <ToolCallBadge key={i} toolCall={tc} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tool results
  if (role === "tool") {
    const results: unknown[] = Array.isArray(msg.content)
      ? msg.content
      : [msg.content];
    return (
      <div className="flex gap-3 pl-10">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {results.map((result, i) => (
            <ToolResultView key={i} result={result} />
          ))}
        </div>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Text Segment Renderer
// ---------------------------------------------------------------------------

function TextSegment({ segment }: { segment: TextSegmentType }) {
  switch (segment.type) {
    case "phase_marker":
      return (
        <div className="mt-1 flex items-center gap-2 py-1.5">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15">
            <CircleDot className="h-3 w-3 text-primary" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">
            {segment.phase}
          </span>
          <div className="flex-1 border-t border-primary/15" />
        </div>
      );

    case "heading":
      return (
        <p
          className={cn(
            "mt-1 font-semibold",
            segment.level <= 2 ? "text-sm" : "text-xs text-muted-foreground",
          )}
        >
          {segment.text}
        </p>
      );

    case "prose":
      return (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
          {segment.text}
        </p>
      );

    case "list_item":
      return (
        <div className="flex items-start gap-2 pl-1 text-sm leading-relaxed text-foreground/90">
          <span className="mt-1.5 shrink-0 text-muted-foreground">&bull;</span>
          <span>{segment.text}</span>
        </div>
      );

    case "json_output":
      return (
        <Collapsible>
          <CollapsibleTrigger className="flex w-fit cursor-pointer items-center gap-2 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 transition-colors hover:bg-emerald-500/15 dark:text-emerald-400">
            <Database className="h-3.5 w-3.5" />
            <span className="font-medium">Structured output generated</span>
            <ChevronDown className="ml-1 h-3 w-3" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="mt-1.5 max-h-60 overflow-auto rounded-md bg-muted/40 p-3 text-[11px] leading-relaxed">
              {tryFormatJson(segment.json)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Tool Call Badge
// ---------------------------------------------------------------------------

function ToolCallBadge({ toolCall }: { toolCall: ToolCallPart }) {
  const name = toolCall.toolName ?? "tool";
  const args: unknown = toolCall.args;
  const { icon, label, detail } = describeToolCall(name, args);

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex w-fit cursor-pointer items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs transition-colors hover:bg-muted">
        {icon}
        <span className="font-medium text-foreground/80">{label}</span>
        {detail && (
          <span className="max-w-[350px] truncate text-muted-foreground">
            {detail}
          </span>
        )}
        <ChevronDown className="ml-auto h-3 w-3 text-muted-foreground" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-muted/30 p-2.5 text-[11px] leading-relaxed">
          {JSON.stringify(args, null, 2)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

function describeToolCall(
  name: string,
  args: unknown,
): { icon: React.ReactNode; label: string; detail: string } {
  const iconClass = "h-3.5 w-3.5 shrink-0";

  switch (name) {
    case "searchDocuments": {
      const query = getStringField(args, "query");
      return {
        icon: <Search className={cn(iconClass, "text-blue-500")} />,
        label: "Searching documents",
        detail: query ? `"${query}"` : "",
      };
    }
    case "getDocumentContent": {
      const documentId = getStringField(args, "documentId");
      return {
        icon: <BookOpen className={cn(iconClass, "text-indigo-500")} />,
        label: "Reading document content",
        detail: documentId ? `doc ...${documentId.slice(-6)}` : "",
      };
    }
    case "getDocumentMetadata": {
      const documentId = getStringField(args, "documentId");
      return {
        icon: <Eye className={cn(iconClass, "text-purple-500")} />,
        label: "Checking document metadata",
        detail: documentId ? `doc ...${documentId.slice(-6)}` : "",
      };
    }
    case "listDocumentsByCategory": {
      const category = getStringField(args, "category");
      return {
        icon: <Database className={cn(iconClass, "text-cyan-500")} />,
        label: "Listing documents",
        detail: category ? `category: ${category}` : "",
      };
    }
    case "getJobDetails":
      return {
        icon: <Shield className={cn(iconClass, "text-slate-500")} />,
        label: "Getting job context",
        detail: "",
      };
    case "flagConcern": {
      const description = getStringField(args, "description");
      return {
        icon: <Flag className={cn(iconClass, "text-red-500")} />,
        label: "Flagging concern",
        detail: description ? description.slice(0, 80) : "",
      };
    }
    case "logActivity": {
      const message = getStringField(args, "message");
      return {
        icon: <Activity className={cn(iconClass, "text-emerald-500")} />,
        label: "Logging progress",
        detail: message ? message.slice(0, 80) : "",
      };
    }
    case "updatePlan": {
      const plan = getArrayField(args, "plan");
      return {
        icon: <CircleDot className={cn(iconClass, "text-primary")} />,
        label: "Updating plan",
        detail: plan
          ? `${plan.length} items (${plan.filter((i) => isRecord(i) && i.status === "done").length} done)`
          : "",
      };
    }
    case "saveFindings": {
      const findings = getArrayField(args, "findings");
      return {
        icon: <BookOpen className={cn(iconClass, "text-amber-600")} />,
        label: "Saving findings",
        detail: findings
          ? `${findings.length} finding${findings.length !== 1 ? "s" : ""}`
          : "",
      };
    }
    case "noteGap": {
      const gapDescription = getStringField(
        isRecord(args) ? args.gap : undefined,
        "description",
      );
      return {
        icon: <AlertTriangle className={cn(iconClass, "text-amber-500")} />,
        label: "Noting data gap",
        detail: gapDescription ? gapDescription.slice(0, 80) : "",
      };
    }
    case "recordInsolvencyIndicator": {
      const indicator = getStringField(
        isRecord(args) ? args.indicator : undefined,
        "indicator",
      );
      return {
        icon: <TrendingDown className={cn(iconClass, "text-red-600")} />,
        label: "Recording insolvency indicator",
        detail: indicator ? indicator.slice(0, 80) : "",
      };
    }
    default:
      return {
        icon: <Wrench className={cn(iconClass, "text-muted-foreground")} />,
        label: name,
        detail: "",
      };
  }
}

// ---------------------------------------------------------------------------
// Tool Result View
// ---------------------------------------------------------------------------

function ToolResultView({ result }: { result: unknown }) {
  const content: unknown = isRecord(result)
    ? (result.result ?? result)
    : result;
  const record = isRecord(content) ? content : undefined;
  const isSuccess = record?.success === true;
  const isError = Boolean(record && (record.error || record.isError));

  if (isSuccess && record && Object.keys(record).length <= 2) {
    return (
      <div className="flex items-center gap-1.5 py-0.5 text-xs text-emerald-600">
        <CheckCircle2 className="h-3 w-3" />
        <span>Done</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-1.5 py-0.5 text-xs text-destructive">
        <XCircle className="h-3 w-3" />
        <span>
          {record && typeof record.error === "string"
            ? record.error.slice(0, 100)
            : "Error"}
        </span>
      </div>
    );
  }

  const preview = getResultPreview(content);

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground">
        <ChevronRight className="h-3 w-3" />
        <span>{preview}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 max-h-48 overflow-auto rounded-md bg-muted/30 p-2.5 text-[11px] leading-relaxed">
          {typeof content === "string"
            ? content.slice(0, 3000)
            : JSON.stringify(content, null, 2).slice(0, 3000)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

function getResultPreview(content: unknown): string {
  if (typeof content === "string") {
    return `${content.slice(0, 80)}${content.length > 80 ? "..." : ""}`;
  }
  if (Array.isArray(content)) {
    return `${content.length} result${content.length !== 1 ? "s" : ""} returned`;
  }
  if (isRecord(content)) {
    if (content.fileName) return String(content.fileName);
    if (content.title) return String(content.title);
    if (content.documents && Array.isArray(content.documents))
      return `${content.documents.length} document${content.documents.length !== 1 ? "s" : ""} found`;
    if (content.results && Array.isArray(content.results))
      return `${content.results.length} match${content.results.length !== 1 ? "es" : ""}`;
    if (content.content && typeof content.content === "string")
      return `${content.content.slice(0, 60)}...`;
    const keys = Object.keys(content);
    return `Result (${keys.slice(0, 3).join(", ")}${keys.length > 3 ? "..." : ""})`;
  }
  return "Result";
}
